import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Monitor, Smartphone, Eye, List, RefreshCw, ExternalLink, Zap, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

const EdmViewer = () => {
  const [edmFiles, setEdmFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [viewMode, setViewMode] = useState('desktop'); // 'desktop' or 'mobile'
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastModified, setLastModified] = useState({});
  const [iframeKey, setIframeKey] = useState(0); // Force iframe remount when needed
  const [copySuccess, setCopySuccess] = useState(false);
  const iframeRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Fetch list of EDM files from the API
  const fetchEdmFiles = async () => {
    try {
      const response = await fetch('/api/edm-files');
      const data = await response.json();
      
      if (response.ok) {
        setEdmFiles(data.files || []);
        if (data.files && data.files.length > 0 && !selectedFile) {
          setSelectedFile(data.files[0]);
        }
      } else {
        console.error('Error fetching EDM files:', data.error);
      }
    } catch (error) {
      console.error('Error fetching EDM files:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check for file modifications
  const checkForUpdates = useCallback(async () => {
    if (!autoRefresh || edmFiles.length === 0 || !selectedFile) return;

    try {
      const response = await fetch('/api/edm-files?includeModified=true');
      const data = await response.json();
      
      if (response.ok && data.files) {
        const newLastModified = {};
        let selectedFileModified = false;
        
        data.files.forEach(fileInfo => {
          const fileName = typeof fileInfo === 'string' ? fileInfo : fileInfo.name;
          const modified = typeof fileInfo === 'string' ? Date.now() : fileInfo.lastModified;
          
          newLastModified[fileName] = modified;
          
          // Check if the currently selected file was modified
          if (fileName === selectedFile && lastModified[fileName] && lastModified[fileName] !== modified) {
            selectedFileModified = true;
          }
        });
        
        // Only update lastModified and trigger iframe reload if there are actual changes
        const hasChanges = JSON.stringify(newLastModified) !== JSON.stringify(lastModified);
        if (hasChanges) {
          setLastModified(newLastModified);
          
          // Only reload iframe if the currently selected file was modified
          if (selectedFileModified) {
            setIframeKey(prev => prev + 1); // This will force iframe remount
          }
        }
        
        // Update file list if there are new files
        const fileNames = data.files.map(f => typeof f === 'string' ? f : f.name);
        if (JSON.stringify(fileNames) !== JSON.stringify(edmFiles)) {
          setEdmFiles(fileNames);
        }
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
    }
  }, [autoRefresh, edmFiles.length, selectedFile, lastModified]);

  useEffect(() => {
    fetchEdmFiles();
  }, []);

  // Set up polling for auto-refresh
  useEffect(() => {
    if (autoRefresh && edmFiles.length > 0) {
      // Set up polling every 3 seconds (increased from 2 to reduce load)
      pollIntervalRef.current = setInterval(checkForUpdates, 3000);
    } else {
      // Clear polling when auto-refresh is disabled
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    // Cleanup on unmount or when autoRefresh changes
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [autoRefresh, edmFiles.length, checkForUpdates]);

  // Update iframe key when selected file changes to force remount
  useEffect(() => {
    if (selectedFile) {
      setIframeKey(prev => prev + 1);
    }
  }, [selectedFile]);

  const getFileDisplayName = (filename) => {
    return filename
      .replace('.html', '')
      .replace(/^\d+-/, '')
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getIframeWidth = () => {
    return viewMode === 'mobile' ? '375px' : '100%';
  };

  const getIframeHeight = () => {
    return viewMode === 'mobile' ? '667px' : '800px';
  };

  const copyHtmlCode = async () => {
    if (!selectedFile) return;
    
    try {
      const response = await fetch(`/edms/${selectedFile}`);
      const htmlContent = await response.text();
      
      await navigator.clipboard.writeText(htmlContent);
      setCopySuccess(true);
      
      // Reset copy success indicator after 2 seconds
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy HTML code:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading EDM files...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">EDM Viewer</h1>
              <p className="text-gray-600">Preview email templates with desktop and mobile breakpoints</p>
            </div>
            {autoRefresh && (
              <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-md">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                Auto-refresh active
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* File List Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <List className="w-5 h-5 text-gray-600" />
                  <h2 className="font-semibold text-gray-900">Email Templates</h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setLoading(true);
                    fetchEdmFiles();
                  }}
                  className="p-2"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-2">
                {edmFiles.map((file) => (
                  <button
                    key={file}
                    onClick={() => setSelectedFile(file)}
                    className={`w-full text-left p-3 rounded-md transition-colors ${
                      selectedFile === file
                        ? 'bg-pink-50 border border-pink-200 text-pink-900'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <div className="font-medium text-sm">
                      {getFileDisplayName(file)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {file}
                    </div>
                  </button>
                ))}
              </div>

              {edmFiles.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No EDM files found</p>
                </div>
              )}
            </div>
          </div>

          {/* Preview Area */}
          <div className="lg:col-span-3">
            {selectedFile ? (
              <div className="bg-white rounded-lg shadow-sm border">
                {/* Controls */}
                <div className="flex items-center justify-between p-4 border-b">
                  <div className="flex items-center gap-2">
                    <Eye className="w-5 h-5 text-gray-600" />
                    <h3 className="font-semibold text-gray-900">
                      {getFileDisplayName(selectedFile)}
                    </h3>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant={autoRefresh ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setAutoRefresh(!autoRefresh)}
                      className={`flex items-center gap-2 transition-all ${
                        autoRefresh 
                          ? 'bg-green-600 hover:bg-green-700 text-white border-green-600' 
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Zap className={`w-4 h-4 ${autoRefresh ? 'text-white' : 'text-gray-600'}`} />
                      Auto-refresh
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyHtmlCode}
                      className="flex items-center gap-2"
                      disabled={copySuccess}
                    >
                      {copySuccess ? (
                        <>
                          <Check className="w-4 h-4 text-green-600" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy Code
                        </>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(`/edms/${selectedFile}?t=${Date.now()}`, '_blank')}
                      className="flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Open in New Tab
                    </Button>
                    <div className="w-px h-6 bg-gray-300 mx-2" />
                    <Button
                      variant={viewMode === 'desktop' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('desktop')}
                      className="flex items-center gap-2"
                    >
                      <Monitor className="w-4 h-4" />
                      Desktop
                    </Button>
                    <Button
                      variant={viewMode === 'mobile' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('mobile')}
                      className="flex items-center gap-2"
                    >
                      <Smartphone className="w-4 h-4" />
                      Mobile
                    </Button>
                  </div>
                </div>

                {/* Preview Frame */}
                <div className="p-4">
                  <div 
                    className={`mx-auto transition-all duration-300 ${
                      viewMode === 'mobile' ? 'max-w-sm' : 'w-full'
                    }`}
                  >
                    <div 
                      className={`border rounded-lg overflow-hidden ${
                        viewMode === 'mobile' ? 'shadow-lg' : 'shadow-sm'
                      }`}
                      style={{
                        width: getIframeWidth(),
                        height: getIframeHeight(),
                        margin: viewMode === 'mobile' ? '0 auto' : '0'
                      }}
                    >
                      <iframe
                        key={iframeKey}
                        ref={iframeRef}
                        src={`/edms/${selectedFile}`}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        title={`EDM Preview: ${selectedFile}`}
                        className="w-full h-full"
                      />
                    </div>
                  </div>
                  
                  {viewMode === 'mobile' && (
                    <div className="text-center mt-4 text-sm text-gray-500">
                      Mobile Preview (375px × 667px)
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border p-8">
                <div className="text-center text-gray-500">
                  <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2">Select an EDM to Preview</h3>
                  <p>Choose an email template from the sidebar to view it here</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EdmViewer;